import { PublicCollectionItems } from "@/config";
import type { OnlyPublicProperties } from "@/types/utility";
import type { InjectionKey, Ref } from "vue";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import * as CategorySelect from "@/services/collection/categories/categoriesSelect";
import { getUrlParamAsBool, getUrlQueryAsNumber, getUrlQueryAsString } from "@/utilities/urlParams";
import { getEnumKeyByValue, isStringInEnum } from "@/utilities/enum";
import { forceAscRangeOrder } from "@/utilities/number";

export const filterInjectionKey = Symbol() as InjectionKey<Ref<OnlyPublicProperties<Filter>>>;

export enum SortBy {
  PriceAsc = "priceMinToMax",
  PriceDesc = "priceMaxToMin",
  MarketplaceListedNewestFirst = "marketplaceListedNewestFirst",
  MarketplaceListedOldestFirst = "marketplaceListedOldestFirst",
  PublishedNewestFirst = "publishedNewestFirst",
  PublishedOldestFirst = "publishedOldestFirst",
  Relevance = "relevance",
  Likes = "likesCountDesc",
}

const DefaultSort = SortBy.PublishedNewestFirst;
const DefaultPage = 0;

export type CategoryFilterValue = CategoryId | typeof CategorySelect.ValueAllCategories;

type PriceLimit = number | undefined;

export type FilterDefaults = {
  fulltext: string;
  categoryId: CategoryFilterValue;
  onlyForSale: boolean;
  sortOrder: SortBy;
  page: number;
  perPage: number;
  excludeMy: boolean;
  nickname: string | undefined;
  priceFrom: PriceLimit;
  priceTo: PriceLimit;
};

export class Filter {
  private _fulltext: string;
  private _categoryId: CategoryFilterValue = CategorySelect.ValueAllCategories;
  private _onlyForSale: boolean;
  private _sortOrder: SortBy;
  private _page = DefaultPage;
  private _perPage = PublicCollectionItems.listing.perPage;
  private _excludeMy = false;
  private _nickname: string | undefined;
  private _priceFrom: PriceLimit;
  private _priceTo: PriceLimit;

  private defaults: FilterDefaults;
  private emptyState: FilterDefaults;

  private _activeCount = 0;

  constructor(customDefaults: Partial<FilterDefaults> = {}, emptyState: Partial<FilterDefaults> = {}) {
    this.defaults = {
      fulltext: "",
      categoryId: CategorySelect.ValueAllCategories,
      onlyForSale: false,
      sortOrder: DefaultSort,
      page: DefaultPage,
      perPage: PublicCollectionItems.listing.perPage,
      nickname: undefined,
      excludeMy: false,
      priceFrom: undefined,
      priceTo: undefined,
      ...customDefaults,
    };

    const receivedEmptyStateWithoutUndefined = Object.fromEntries(Object.entries(emptyState).filter(([_, value]) => value !== undefined));
    this.emptyState = {
      fulltext: "",
      categoryId: CategorySelect.ValueAllCategories,
      onlyForSale: false,
      sortOrder: DefaultSort,
      page: 0,
      perPage: PublicCollectionItems.listing.perPage,
      nickname: undefined,
      excludeMy: false,
      priceFrom: this.defaults.priceFrom,
      priceTo: this.defaults.priceTo,
      ...receivedEmptyStateWithoutUndefined,
    };

    this.reset();
  }

  public fillFromUrl(): Filter {
    this.reset();

    try {
      this.fulltext = getUrlQueryAsString("fulltext");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.categoryId = getUrlQueryAsNumber("categoryId");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.onlyForSale = getUrlParamAsBool("onlyForSale", true);
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.priceFrom = getUrlQueryAsNumber("priceFrom");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.priceTo = getUrlQueryAsNumber("priceTo");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.excludeMy = getUrlParamAsBool("excludeMy", true);
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      const sort = getUrlQueryAsString("sortOrder");
      const isValid = isStringInEnum(sort, SortBy);
      if (isValid) {
        this.sortOrder = getEnumKeyByValue(sort, SortBy);
      }
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.page = getUrlQueryAsNumber("page");
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    return this;
  }

  public reset(): void {
    this.fulltext = this.defaults.fulltext;
    this.categoryId = this.defaults.categoryId;
    this.onlyForSale = this.defaults.onlyForSale;
    this.sortOrder = this.defaults.sortOrder;
    this.page = this.defaults.page;
    this._perPage = this.defaults.perPage;
    this.excludeMy = this.defaults.excludeMy;
    this.priceFrom = this.defaults.priceFrom;
    this.priceTo = this.defaults.priceTo;
    this._nickname = this.defaults.nickname;
  }

  public isSomeSpecificCategorySelected(): boolean {
    return this.categoryId !== CategorySelect.ValueAllCategories;
  }

  public isSomethingSelectedExceptCategory(): boolean {
    return (
      this.fulltext !== "" || //
      this.onlyForSale !== true ||
      this.excludeMy === true ||
      this.priceFrom !== undefined ||
      this.priceTo !== undefined
    );
  }

  /** Active filters counting */
  public get activeCount(): number {
    return this._activeCount;
  }

  private recountActiveFilters(): void {
    let c = 0;
    if (this.fulltext !== this.emptyState.fulltext) {
      c++;
    }
    if (this.categoryId !== this.emptyState.categoryId) {
      c++;
    }
    if (this.onlyForSale !== this.emptyState.onlyForSale) {
      c++;
    }
    if (this.priceFrom !== this.emptyState.priceFrom || this.priceTo !== this.emptyState.priceTo) {
      c++;
    }
    this._activeCount = c;
  }

  /** Filter params setters / getters */

  public set fulltext(newValue: string) {
    this._fulltext = newValue;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get fulltext(): string {
    return this._fulltext;
  }

  public set categoryId(newCategoryId: CategoryFilterValue) {
    this._categoryId = newCategoryId;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get categoryId(): CategoryFilterValue {
    return this._categoryId;
  }

  public set sortOrder(newSortOrder: SortBy) {
    this._sortOrder = newSortOrder;
    this.page = this.defaults.page;
  }

  public get sortOrder(): SortBy {
    return this._sortOrder;
  }

  public set page(newPage: number) {
    this._page = newPage;
  }

  public get page(): number {
    return this._page;
  }

  public set perPage(newPerPage: number) {
    this._perPage = newPerPage;
  }

  public get perPage(): number {
    return this._perPage;
  }

  public set priceFrom(newMinPrice: PriceLimit) {
    this._priceFrom = newMinPrice;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get priceFrom(): PriceLimit {
    return this._priceFrom;
  }

  public set priceTo(newMaxPrice: PriceLimit) {
    this._priceTo = newMaxPrice;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get priceTo(): PriceLimit {
    return this._priceTo;
  }

  public set onlyForSale(onlyForSale: boolean) {
    this._onlyForSale = onlyForSale;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get onlyForSale(): boolean {
    return this._onlyForSale;
  }

  public set excludeMy(exclude: boolean) {
    this._excludeMy = exclude;
    this.page = this.defaults.page;
  }

  public get excludeMy(): boolean {
    return this._excludeMy;
  }

  public set nickname(nickname: string | undefined) {
    this._nickname = nickname;
  }

  public get nickname(): string | undefined {
    return this._nickname;
  }
}

export function createApiParams(filter: OnlyPublicProperties<Filter>): Record<string, number | CategoryId | CategoryId[] | string | boolean | undefined> {
  const q = filter.fulltext;
  const fulltext = q === undefined || q === "" ? undefined : q;
  const [priceFrom, priceTo] = forceAscRangeOrder(filter.priceFrom, filter.priceTo);

  const params = <Record<string, number | CategoryId[] | string | boolean | undefined>>{
    fulltext,
    onlyForSale: filter.onlyForSale ? 1 : 0,
    sortOrder: filter.sortOrder,
    page: filter.page,
    perPage: filter.perPage,
    excludeMy: filter.excludeMy ? 1 : 0,
    nickname: filter.nickname,
    minPrice: priceFrom,
    maxPrice: priceTo,
  };

  params["categoryId"] = filter.categoryId === CategorySelect.ValueAllCategories ? undefined : filter.categoryId;

  return params;
}

export function createUrlParams(filter: OnlyPublicProperties<Filter>) {
  const q = filter.fulltext;
  const fulltext = q === undefined || q === "" ? undefined : q;
  const [priceFrom, priceTo] = forceAscRangeOrder(filter.priceFrom, filter.priceTo);

  const params = <Record<string, number | CategoryFilterValue | string | undefined>>{
    categoryId: filter.categoryId,
    fulltext,
    onlyForSale: filter.onlyForSale ? 1 : 0,
    sortOrder: filter.sortOrder,
    page: filter.page,
    perPage: filter.perPage,
    priceFrom,
    priceTo,
  };

  return params;
}

export function updateCurrentUrl(filter: OnlyPublicProperties<Filter>) {
  const params = createUrlParams(filter);
  // Lazy import breaks circular dependency: router.ts → registerGuards → startupUserCheck (TDZ)
  // when this module is reached via linkFactories.ts during the auth bootstrap chain.
  void import("@/router/router").then(({ router }) => router.replace({ path: router.currentRoute.value.path, query: params }));
}
